var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useQuery } from '@tanstack/react-query';
import { HandshakePainterServiceApiV1 } from 'src/api/handshakeApi';
import { useAnalyticsContext } from 'src/common/metrics';
import { useDefaultApiParams } from './useDefaultApiParams';
/** Timeout for the compliance API call. Start at 5s (conservative for mobile networks + cold starts).
 *  Tune down based on COMPLIANCE_HIGH_PRIORITY_API_LATENCY p99 data. */
export const COMPLIANCE_API_TIMEOUT_MS = 5000;
/**
 * Custom hook to fetch high-priority product compliance data based on a single ASIN
 */
export const useProductComplianceHighPriority = ({ asin, enabled = true }) => {
    const { getDefaultParams } = useDefaultApiParams();
    const rumClient = useAnalyticsContext();
    return useQuery({
        queryKey: ['getProductComplianceHighPriority', asin],
        queryFn: (_a) => __awaiter(void 0, [_a], void 0, function* ({ signal: querySignal }) {
            var _b;
            const defaultApiParams = getDefaultParams();
            let signal = querySignal;
            try {
                signal = AbortSignal.any([querySignal, AbortSignal.timeout(COMPLIANCE_API_TIMEOUT_MS)]);
            }
            catch (_c) {
                // Gracefully degrade to querySignal on browsers that don't support AbortSignal.any/timeout
            }
            const start = Date.now();
            try {
                const result = yield HandshakePainterServiceApiV1.getProductComplianceHighPriority({
                    asin: asin,
                    xUserLinked: defaultApiParams.xUserLinked,
                    xJoinId: defaultApiParams.xJoinId,
                    marketplaceId: defaultApiParams.marketplaceId,
                }, { signal });
                rumClient.recordEvent('COMPLIANCE_HIGH_PRIORITY_API_LATENCY', {
                    asin,
                    durationMs: Date.now() - start,
                    status: 'success',
                });
                return result;
            }
            catch (e) {
                const isAbortOrTimeout = e instanceof DOMException && (e.name === 'AbortError' || e.name === 'TimeoutError');
                rumClient.recordEvent('COMPLIANCE_HIGH_PRIORITY_API_LATENCY', {
                    asin,
                    durationMs: Date.now() - start,
                    status: isAbortOrTimeout ? 'cancelled' : 'error',
                });
                if (!isAbortOrTimeout) {
                    const error = e;
                    rumClient.recordEvent('COMPLIANCE_HIGH_PRIORITY_API_ERROR', {
                        asin,
                        errorTitle: error === null || error === void 0 ? void 0 : error.title,
                        errorDetails: (_b = error === null || error === void 0 ? void 0 : error.detail) !== null && _b !== void 0 ? _b : String(error),
                    });
                }
                throw e;
            }
        }),
        enabled: enabled && !!asin,
    });
};
